import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { CALENDAR_EVENT_COLORS, COLORS } from '../constants/theme';
import { useEventsCalendar } from '../hooks/useEventsCalendar';
import { CalendarEventType, CalendarDayEvents } from '../types';
import { OwnershipScope } from '../types/favourites';
import {
  fromMonthIndex,
  getCalendarMonthBounds,
  groupCalendarDays,
  toIsoDate,
} from '../utils/eventsCalendarRange';
import { SkeletonCard } from './SkeletonLoader';

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

const WEEK_DAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const;

const LEGEND_ITEMS: ReadonlyArray<{ readonly type: CalendarEventType; readonly label: string }> = [
  { type: 'observation', label: 'Autocall Observation' },
  { type: 'callable', label: 'Issuer Callable Observation' },
  { type: 'maturity', label: 'Maturity' },
];

interface EventsCalendarProps {
  readonly includeWarrants?: boolean;
  readonly ownershipScope?: OwnershipScope;
  readonly onDayPress?: (date: string) => void;
}

interface DayCellProps {
  readonly day: number | null;
  readonly isoDate: string | null;
  readonly events: CalendarDayEvents | undefined;
  readonly isToday: boolean;
  readonly onPress?: (date: string) => void;
}

const DayMarker: React.FC<{ readonly types: readonly CalendarEventType[] }> = ({ types }) => (
  <View style={styles.marker}>
    {types.map((type) => (
      <View
        key={type}
        style={[styles.markerSlice, { backgroundColor: CALENDAR_EVENT_COLORS[type], flex: 1 }]}
      />
    ))}
  </View>
);

const DayCell: React.FC<DayCellProps> = ({ day, isoDate, events, isToday, onPress }) => {
  if (day === null || !isoDate) {
    return <View style={styles.dayCell} />;
  }

  const types = (events?.types ?? []).filter((type) => type in CALENDAR_EVENT_COLORS);
  const hasEvents = types.length > 0;

  return (
    <TouchableOpacity
      style={styles.dayCell}
      onPress={() => {
        if (hasEvents) {
          onPress?.(isoDate);
        }
      }}
      disabled={!hasEvents}
      accessibilityRole="button"
      accessibilityLabel={
        hasEvents ? `${isoDate}, ${types.join(', ')}` : isoDate
      }
      accessibilityState={{ disabled: !hasEvents }}
    >
      <View
        style={[
          styles.dayCircle,
          isToday && styles.todayCircle,
          !hasEvents && isToday && styles.todayEmptyCircle,
        ]}
      >
        {hasEvents ? <DayMarker types={types} /> : null}
        <Text
          style={[
            styles.dayText,
            hasEvents && styles.dayTextOnMarker,
            isToday && !hasEvents && styles.todayText,
          ]}
        >
          {day}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

export const EventsCalendar: React.FC<EventsCalendarProps> = ({
  includeWarrants = true,
  ownershipScope = 'group',
  onDayPress,
}) => {
  const bounds = useMemo(() => getCalendarMonthBounds(), []);
  const [viewIndex, setViewIndex] = useState(bounds.currentIndex);
  const { days, isLoading, isError, refetch } = useEventsCalendar(includeWarrants, ownershipScope);
  const eventsByDate = useMemo(() => groupCalendarDays(days), [days]);

  const { year, month } = fromMonthIndex(viewIndex);
  const canGoPrev = viewIndex > bounds.minIndex;
  const canGoNext = viewIndex < bounds.maxIndex;

  const weeks = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const mondayOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const cells: Array<number | null> = [];
    for (let i = 0; i < mondayOffset; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      cells.push(day);
    }
    while (cells.length % 7 !== 0) {
      cells.push(null);
    }

    const rows: Array<Array<number | null>> = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    return rows;
  }, [year, month]);

  const todayIso = toIsoDate(new Date());

  if (isLoading) {
    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Events calendar</Text>
        <SkeletonCard />
      </View>
    );
  }

  if (isError) {
    return (
      <View style={styles.section}>
        <View style={styles.headerRow}>
          <Text style={styles.sectionTitle}>Events calendar</Text>
          <TouchableOpacity onPress={() => refetch()} activeOpacity={0.7}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
        <Text style={styles.errorText}>Error loading calendar events</Text>
      </View>
    );
  }

  return (
    <View style={styles.section}>
      <Text style={styles.sectionTitle}>Events calendar</Text>

      <View style={styles.calendarBody}>
        <View style={styles.monthRow}>
          <TouchableOpacity
            onPress={() => setViewIndex((current) => current - 1)}
            disabled={!canGoPrev}
            style={styles.navButton}
            accessibilityLabel="Previous month"
          >
            <Text style={[styles.navButtonText, !canGoPrev && styles.navButtonDisabled]}>‹</Text>
          </TouchableOpacity>
          <Text style={styles.monthTitle}>
            {MONTH_NAMES[month]} {year}
          </Text>
          <TouchableOpacity
            onPress={() => setViewIndex((current) => current + 1)}
            disabled={!canGoNext}
            style={styles.navButton}
            accessibilityLabel="Next month"
          >
            <Text style={[styles.navButtonText, !canGoNext && styles.navButtonDisabled]}>›</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.weekDaysRow}>
          {WEEK_DAYS.map((label, index) => (
            <View key={`${label}-${index}`} style={styles.weekDayCell}>
              <Text style={styles.weekDayText}>{label}</Text>
            </View>
          ))}
        </View>

        <View style={styles.daysGrid}>
          {weeks.map((week, weekIndex) => (
            <View key={weekIndex} style={styles.weekRow}>
              {week.map((day, dayIndex) => {
                const isoDate =
                  day === null
                    ? null
                    : `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                return (
                  <DayCell
                    key={dayIndex}
                    day={day}
                    isoDate={isoDate}
                    events={isoDate ? eventsByDate.get(isoDate) : undefined}
                    isToday={isoDate === todayIso}
                    onPress={onDayPress}
                  />
                );
              })}
            </View>
          ))}
        </View>
      </View>

      <View style={styles.legendRow}>
        {LEGEND_ITEMS.map((item) => (
          <View key={item.type} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: CALENDAR_EVENT_COLORS[item.type] }]} />
            <Text style={styles.legendLabel}>{item.label}</Text>
          </View>
        ))}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    width: '100%',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: COLORS.text,
    fontFamily: 'Montserrat_600SemiBold',
    marginBottom: 8,
  },
  retryText: {
    fontSize: 13,
    color: COLORS.primary,
    fontWeight: '600',
  },
  errorText: {
    fontSize: 13,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_400Regular',
  },
  calendarBody: {
    width: '100%',
  },
  monthRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  navButton: {
    paddingVertical: 4,
    paddingHorizontal: 6,
    minWidth: 32,
    alignItems: 'center',
  },
  navButtonText: {
    fontSize: 20,
    color: COLORS.primary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  navButtonDisabled: {
    color: COLORS.lightGray,
  },
  monthTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: COLORS.text,
    fontFamily: 'Montserrat_600SemiBold',
    textTransform: 'capitalize',
  },
  weekDaysRow: {
    flexDirection: 'row',
    marginBottom: 6,
  },
  weekDayCell: {
    flex: 1,
    alignItems: 'center',
  },
  weekDayText: {
    fontSize: 11,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
  },
  daysGrid: {
    width: '100%',
  },
  weekRow: {
    flexDirection: 'row',
  },
  dayCell: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 5,
    paddingHorizontal: 0,
  },
  dayCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  todayCircle: {
    borderWidth: 2,
    borderColor: COLORS.primary,
  },
  todayEmptyCircle: {
    backgroundColor: 'transparent',
  },
  marker: {
    ...StyleSheet.absoluteFillObject,
    flexDirection: 'row',
  },
  markerSlice: {
    height: '100%',
  },
  dayText: {
    fontSize: 12,
    color: COLORS.text,
    fontFamily: 'Montserrat_500Medium',
    zIndex: 1,
  },
  dayTextOnMarker: {
    color: COLORS.white,
    fontFamily: 'Montserrat_600SemiBold',
  },
  todayText: {
    color: COLORS.primary,
    fontFamily: 'Montserrat_600SemiBold',
  },
  legendRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
  },
  legendLabel: {
    fontSize: 12,
    color: COLORS.textSecondary,
    fontFamily: 'Montserrat_500Medium',
    flexShrink: 1,
  },
});
